import { NavLink, useLocation } from 'react-router-dom';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Toolbar from '@mui/material/Toolbar';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { NAV_ITEMS } from './navigation';
import { useAuth } from '../app/AuthProvider';

export const SIDEBAR_WIDTH = 248;
export const COLLAPSED_WIDTH = 72;

interface AppSidebarProps {
  collapsed: boolean;
  mobileOpen: boolean;
  onCloseMobile: () => void;
}

function SidebarContent({ collapsed, onNavigate }: { collapsed: boolean; onNavigate?: () => void }) {
  const location = useLocation();
  const { user } = useAuth();

  return (
    <>
      <Toolbar sx={{ px: collapsed ? 1 : 2 }}>
        {collapsed ? (
          <Typography variant="h6" color="primary" sx={{ mx: 'auto' }}>
            HC
          </Typography>
        ) : (
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
              HiveCore CRM
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Operations Platform
            </Typography>
          </Box>
        )}
      </Toolbar>

      <List sx={{ px: 1 }}>
        {NAV_ITEMS.filter((item) => user && item.roles.includes(user.role)).map((item) => {
          const active = location.pathname === item.to || location.pathname.startsWith(`${item.to}/`);
          return (
            <Tooltip key={item.to} title={collapsed ? item.label : ''} placement="right">
              <ListItemButton
                component={NavLink}
                to={item.to}
                onClick={onNavigate}
                selected={active}
                sx={{
                  borderRadius: 1.5,
                  mb: 0.5,
                  minHeight: 44,
                  justifyContent: collapsed ? 'center' : 'flex-start',
                  '&.Mui-selected': {
                    bgcolor: 'primary.main',
                    color: 'primary.contrastText',
                    '& .MuiListItemIcon-root': { color: 'inherit' },
                    '&:hover': { bgcolor: 'primary.dark' },
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: collapsed ? 0 : 40, justifyContent: 'center' }}>
                  {item.icon}
                </ListItemIcon>
                {collapsed ? null : <ListItemText primary={item.label} />}
              </ListItemButton>
            </Tooltip>
          );
        })}
      </List>
    </>
  );
}

export function AppSidebar({ collapsed, mobileOpen, onCloseMobile }: AppSidebarProps) {
  const width = collapsed ? COLLAPSED_WIDTH : SIDEBAR_WIDTH;

  return (
    <Box component="nav" sx={{ width: { md: width }, flexShrink: { md: 0 } }}>
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onCloseMobile}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { width: SIDEBAR_WIDTH, boxSizing: 'border-box' },
        }}
      >
        <SidebarContent collapsed={false} onNavigate={onCloseMobile} />
      </Drawer>

      <Drawer
        variant="permanent"
        open
        sx={{
          display: { xs: 'none', md: 'block' },
          '& .MuiDrawer-paper': {
            width,
            boxSizing: 'border-box',
            borderRight: '1px solid',
            borderColor: 'divider',
            overflowX: 'hidden',
            transition: 'width 180ms ease',
          },
        }}
      >
        <SidebarContent collapsed={collapsed} />
      </Drawer>
    </Box>
  );
}
